import classNames from 'classnames';
import React, { useState } from 'react';
import LazyloadImage from '../../sharedComponents/LazyloadImage';
import './video.scss';

const headlineClasses = classNames(
    'eds-g-cell',
    'eds-g-cell-12-12',
    'eds-g-offset-0-12',
    'eds-g-cell-md-8-12',
    'eds-g-offset-md-2-12',
    'eds-g-offset-md-2-12',
    'eds-g-cell-lg-6-12',
    'eds-g-offset-lg-3-12',
);

const videoClasses = classNames(
    'eds-g-cell',
    'eds-g-cell-12-12',
    'eds-g-offset-0-12',
    'eds-g-cell-md-10-12',
    'eds-g-offset-md-1-12',
    'eds-l-mar-top-14',
    'eds-g-cell--has-overflow',
);

const playButtonIcon =
    'https://eventbrite-s3.s3.amazonaws.com/marketing/landingpages/assets/on-demand-video/play_large_icon.svg';

interface VideoProps {
    tagline: string;
    headline: string;
    description: string;
    video: string;
    backgroundImage: string;
    moduleClass: string;
    alt?: string;
}

interface IFrameProps {
    autoplay?: boolean;
    controls?: boolean;
    mute?: boolean;
    fullscreen?: boolean;
    loop?: boolean;
    videoID: string;
}

const IFrameVideo = ({
    autoplay,
    mute,
    controls,
    fullscreen,
    loop,
    videoID,
}: IFrameProps) => {
    const videoControls: string[] = [
        `origin=https://eventbrite.com`, // change to env specific???
        'modestbranding=1',
        'rel=0', // makes sure any related videos shown are from the same channel
        `playlist=${videoID}`, // causes the same video to loop when combined with loop=1
        `mute=${mute ? '1' : '0'}`,
        `autoplay=${autoplay ? '1' : '0'}`,
        `fs=${fullscreen ? '1' : '0'}`,
        `loop=${loop ? '1' : '0'}`,
        `controls=${controls ? '1' : '0'}`,
    ];
    const videoControlsStr =
        videoControls.length > 0 ? `?${videoControls.join('&')}` : '';
    return (
        <iframe
            role="video"
            id="ytplayer"
            className="mktg-video-card__image"
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoID}${videoControlsStr}`}
            frameBorder="0"
        ></iframe>
    );
};

const VideoOrThumbnail = ({
    headline,
    backgroundImage,
    alt,
    videoProps,
}: { videoProps: IFrameProps } & Partial<VideoProps>) => {
    const [isPlaying, setPlaying] = useState(false);

    if (isPlaying) {
        return <IFrameVideo {...videoProps} />;
    } else {
        return (
            <div onClick={() => setPlaying(true)}>
                <img
                    alt={alt || headline}
                    src={backgroundImage}
                    className="mktg-video-card__image thumbnail"
                />
                <LazyloadImage
                    alt={'custom play button'}
                    image={playButtonIcon}
                    classes="thumbnail-overlay"
                    style={{ position: 'absolute' }} // have to override LazyloadImage styling
                />
            </div>
        );
    }
};

const getVideoID = (videoURL: string) => {
    return videoURL.split('v=')[1];
};

const VideoModule = ({
    tagline,
    headline,
    description,
    video: videoUrl,
    backgroundImage,
    alt,
    moduleClass,
}: VideoProps) => {
    const videoProps = {
        controls: true,
        loop: true,
        videoID: getVideoID(videoUrl),
    };

    const videoThumbnailProps = {
        headline,
        backgroundImage,
        alt,
        videoProps,
    };

    return (
        <div className={moduleClass}>
            <section className="eds-bg-color--white mktg-top-p-180">
                <div className="marketing-container__max-width eds-l-pad-hor-6">
                    <div className="eds-g-grid">
                        <div className="eds-l-mar-top-6">
                            <div className={headlineClasses}>
                                <p className="eds-text-color--ui-orange eds-text-bl mktg-text-weight--heavy eds-text--center">
                                    {tagline}
                                </p>
                                <h2 className="eds-text-color--grey-900 mktg-h1 mktg-text-weight--heavy eds-text--center  eds-l-mar-top-6">
                                    {headline}
                                </h2>
                                <div
                                    className="eds-text-color--grey-700 eds-text-bl eds-text-weight--regular eds-l-mar-top-6  eds-text--center"
                                    //eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: description,
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section>
                <div className="marketing-container__max-width eds-l-pad-hor-6">
                    <div className="eds-g-grid">
                        <div className={videoClasses}>
                            <div className="mktg-video-card mktg-video-card_hero mktg-shadow-primary">
                                <div className="mktg-video-card__header">
                                    <VideoOrThumbnail
                                        {...videoThumbnailProps}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
export default VideoModule;
